import { RoomService as ApiRoomService } from '../../../shared-api/room.service';
import { inject, Injectable } from '@angular/core';
import { RoomProperties } from '../../../shared-api/roomProperties';
import { Room } from '../../../shared-api/room';
import { MatrixService } from '../matrix/matrix.service';
import { RoomHelperService } from './room-helper.service';
import { CreateRoomRequest } from '../../../shared-api/createRoomRequest';
import { Rooms } from '../../../shared-api/rooms';
import {
  EventType,
  HistoryVisibility,
  IPublicRoomsChunkRoom,
  IStateEventWithRoomId,
  JoinRule,
  MatrixClient,
  MatrixError,
  MatrixEvent,
  Preset,
  Room as MatrixRoom,
  Visibility
} from 'matrix-js-sdk';
import { RoomInvite } from '../../../shared-api/roomInvite';
import { StateEvent, StateEventService } from '../state-event/state-event.service';
import {
  changeRoomAccess,
  changeRoomEncryption,
  changeRoomHistoryVisibility,
  changeRoomIsFederated,
  changeRoomJoinRule,
  changeRoomName,
  changeRoomTheme
} from './change-room-settings-helper';
import { ICreateRoomOpts } from 'matrix-js-sdk/lib/@types/requests';
import {
  createRoomAndWait,
  forgetRoomAndWait,
  inviteAndWaitAll,
  joinRoomAndWait,
  leaveRoomAndWait
} from './room-wait-helper';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership';
import { format } from '../util';
import { RoomStates } from '../../../shared-api/roomStates';
import { RoomState } from '../../../shared-api/roomState';
import { sleep } from 'matrix-js-sdk/lib/utils';
import { HttpStatus, jsonError, matrixErrorAsJson } from '../../errors/error-with-status';

const REQ_PARAM_ERROR = 'Required parameter {0} was null or undefined when calling {1}.';
const ROOM_NOT_FOUND_ERROR = 'No room with {0} was found.';

const SLEEP_TIMEOUT = 1000;

// Obergrenze für den einmaligen, nicht paginierten Abruf des öffentlichen Raumverzeichnisses.
// Reicht für die (kleinen) Test-Homeserver der gematik-Testsuite.
const PUBLIC_ROOMS_DIRECTORY_LIMIT = 1000;

/**
 * Angereicherter Eintrag aus dem öffentlichen Raumverzeichnis für die UI: das Room-DTO plus
 * Mitgliederzahl, Alias und Beitrittsstatus, die das DTO selbst nicht trägt.
 */
export interface PublicRoomInfo {
  room: Room;
  canonicalAlias?: string;
  memberCount: number;
  joined: boolean;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Die RoomService-Klasse stellt das Matrix-RaumManagement bereit.
 * Begründung: Hier werden die allgemeinen Methoden bereitgestellt zur Verwaltung von Räumen.
 * Implementation of the mandatory Room API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 *  A_25363 - Testtreiber-Modul
 *  A_25361 - keine Modifikation der Inhalte
 *  A_25813-01 - Pflichtparameter bei der Chatroom-Erzeugung für föderierte und intersektorale Kommunikation
 *  A_25426 - Verwendung des Chatroom-Typen für föderierte und intersektorale Kommunikation
 *  A_25046 - Durchsetzung der Berechtigungskonfiguration - Client
 *  A_25816 - Der TI-M Client MUSS das custom state event aus der AFO erzeugen können.
 *  A_26338-02 - In Räumen mit Custom Room Types, die der AFO entnommen werden können, MUSS der Client Custom State
 *  Events, die der AFO entnommen werden können, zuzüglich zu den standard events verwenden.
 *  A_26347 - Hinweis vor Einladung weiterer Chatteilnehmer
 *  A_25609 Erinnerung an die Raumlöschung
 *  A_25611 Durchführung der Löschung
 *  A_28342 Räume können über leave und forget verlassen werden
 *  A_25325-01 Öffentliche Röume mit creation_content m.federate=false erstellen
 *  A_28595 - gematik Raumtyp V2
 */
export class RoomService implements ApiRoomService {
  private readonly matrix = inject(MatrixService);
  private readonly stateEventService = inject(StateEventService);

  /**
   * Changes the room settings
   * @param roomId ID of the room
   * @param roomProperties
   */
  async changeRoomSettings(roomId: string | undefined, roomProperties: RoomProperties): Promise<Room> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'changeRoomSettings'));
    }
    if (!roomProperties) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomProperties', 'changeRoomSettings'));
    }

    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(format(ROOM_NOT_FOUND_ERROR, roomId));
    }

    // Gesammelte StateEvents
    const stateEvents: StateEvent[] = [
      ...changeRoomName(room, roomProperties.name),
      ...changeRoomTheme(room, roomProperties.theme),
      ...changeRoomAccess(room, roomProperties.roomAccess),
      ...changeRoomJoinRule(room, roomProperties.joinRule),
      ...changeRoomHistoryVisibility(room, roomProperties.historyVisibility),
      ...changeRoomIsFederated(room, roomProperties.isFederated),
      ...changeRoomEncryption(room, roomProperties.isEncrypted)
    ];
    if (stateEvents.length > 0) {
      const results = await this.stateEventService.sendAndWaitAll(this.matrix.client, stateEvents);

      let firstError: Error | undefined = undefined;
      for (const r of results) {
        if (r.success) {
          console.debug('Event angekommen:', r.value?.getContent());
        } else {
          console.warn('Event fehlgeschlagen:', r.event, r.reason);
          firstError ??= r.reason;
        }
      }

      if (firstError) {
        console.error('Fehler beim Senden aller StateEvents', firstError);

        if (!(firstError instanceof MatrixError)) {
          throw firstError;
        }
        throw matrixErrorAsJson('SendStateEventError', firstError);
      }
    }

    await sleep(SLEEP_TIMEOUT);
    const apiRoom = RoomHelperService.toApiRoom(room);

    // s. Kommentar in createRoom!
    apiRoom.name = roomProperties.name;
    apiRoom.theme = roomProperties.theme;

    return apiRoom;
  }

  /**
   * Create a room.
   * @param createRoomRequest
   */
  async createRoom(createRoomRequest: CreateRoomRequest): Promise<Room> {
    if (!createRoomRequest) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomRequest', 'createRoom'));
    }

    // TI-Vorgabe: private Räume MÜSSEN verschlüsselt sein, öffentliche Räume MÜSSEN unverschlüsselt sein.
    // Eine inkonsistente Anforderung im Body wird mit HTTP 403 abgelehnt. Unbestimmtes isEncrypted
    // (undefined/null) ist erlaubt; die Verschlüsselung wird strikt aus roomAccess abgeleitet, das
    // Feld dient nur als Konsistenzcheck.
    if (createRoomRequest.roomAccess !== 'public' && createRoomRequest.isEncrypted === false) {
      throw jsonError(
        'CreatePrivateUnencryptedRoomNotAllowedError',
        HttpStatus.Forbidden,
        'A private room must be encrypted'
      );
    }
    if (createRoomRequest.roomAccess === 'public' && createRoomRequest.isEncrypted === true) {
      throw jsonError(
        'CreatePublicEncryptedRoomNotAllowedError',
        HttpStatus.Forbidden,
        'A public room must be unencrypted'
      );
    }

    const roomOptions = RoomService.buildCreateRoomOptions(createRoomRequest);

    try {
      const room = await createRoomAndWait(this.matrix.client, roomOptions);

      await sleep(SLEEP_TIMEOUT);
      const apiRoom = RoomHelperService.toApiRoom(room);

      // Mit client.createRoom wurde ein Raum erzeugt. Dabei wurde bereits ein ClientEvent.Room versendet.
      // Damit ist garantiert, dass Matrix einen Raum mit den gewünschten Eigenschaften anlegt.
      // Dieser wird allerdings asynchron aktualisiert. Insbesondere ist nicht garantiert, dass zum jetzigen Zeitpunkt
      // der richtige Raumname in room eingetragen ist. Matrix macht das "unter der Haube" etwas später.
      // Um Wartezeit zu vermeiden, wird der erwartete Raumname (und Theme) explizit in den Rückgabewert eingetragen.
      apiRoom.name = createRoomRequest.name ?? room.name;
      apiRoom.theme = createRoomRequest.theme;

      return apiRoom;
    } catch (error) {
      console.error('Fehler beim Erstellen/Warten auf Raum', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('CreateRoomError', error);
    }
  }

  /**
   * Baut die ICreateRoomOpts für matrix-js-sdk aus dem CreateRoomRequest.
   * Defaults nach roomAccess; explizite Body-Werte haben Vorrang. null wird wie undefined behandelt.
   */
  private static buildCreateRoomOptions(req: CreateRoomRequest): ICreateRoomOpts {
    const isV2 = !!req.isV2Room;
    const isPublic = req.roomAccess === 'public';

    // Verschlüsselung wird strikt aus roomAccess abgeleitet (Konsistenz wurde oben validiert).
    // Default-Sichtbarkeit:    öffentliche Räume "shared", private Räume "invited".
    // Default-m.federate (A_25325-01): öffentliche Räume nicht föderiert, private Räume föderiert.
    const isEncrypted = !isPublic;
    const historyVisibility =
      req.historyVisibility ?? (isPublic ? HistoryVisibility.Shared : HistoryVisibility.Invited);
    const isFederated = req.isFederated ?? !isPublic;

    const opts: ICreateRoomOpts = {
      creation_content: {
        type: isV2 ? 'de.gematik.tim.roomtype.default.v2' : 'de.gematik.tim.roomtype.default.v1',
        'm.federate': isFederated
      },
      initial_state: RoomService.buildInitialState(req, isV2, isEncrypted, historyVisibility),
      visibility: isPublic ? Visibility.Public : Visibility.Private,
      preset: isPublic ? Preset.PublicChat : Preset.PrivateChat,
      invite: req.invitedMembers?.map(mxid => mxid.mxid)
    };

    if (req.alias) opts.room_alias_name = req.alias;
    if (req.roomVersion) opts.room_version = req.roomVersion;
    return opts;
  }

  private static buildInitialState(
    req: CreateRoomRequest,
    isV2: boolean,
    isEncrypted: boolean,
    historyVisibility: HistoryVisibility | string
  ): ICreateRoomOpts['initial_state'] {
    const name = req.name ?? '';
    const topic = req.theme ?? '';

    return [
      {
        type: isV2 ? 'de.gematik.tim.room.default.v2' : 'de.gematik.tim.room.default.v1',
        state_key: '',
        content: {}
      },
      {
        type: EventType.RoomGuestAccess,
        state_key: '',
        content: { guest_access: 'can_join' }
      },
      ...(isEncrypted
        ? [
            {
              type: EventType.RoomEncryption,
              state_key: '',
              content: { algorithm: 'm.megolm.v1.aes-sha2' }
            }
          ]
        : []),
      ...(isV2 ? [] : [{ type: 'de.gematik.tim.room.name', state_key: '', content: { name } }]),
      { type: EventType.RoomName, state_key: '', content: { name } },
      ...(isV2 ? [] : [{ type: 'de.gematik.tim.room.topic', state_key: '', content: { topic } }]),
      { type: EventType.RoomTopic, state_key: '', content: { topic } },
      {
        type: EventType.RoomHistoryVisibility,
        state_key: '',
        content: { history_visibility: historyVisibility }
      }
    ];
  }

  /**
   * Forget this room (deny room invite) A_28342
   * @param roomId ID of the room
   */
  async forgetRoom(roomId: string): Promise<void> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'forgetRoom'));
    }

    try {
      this.setRoomForgotten(roomId);
      await forgetRoomAndWait(this.matrix.client, roomId);
    } catch (error) {
      this.unsetRoomForgotten(roomId);
      console.error('Fehler beim Vergessen/Warten', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('ForgetRoomError', error);
    }
  }

  /**
   * Get the room settings
   * @param roomId ID of the room
   */
  async getRoomSettings(roomId: string): Promise<RoomProperties> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'getRoomSettings'));
    }

    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(`No room with Id "${roomId}" found`);
    }

    return RoomHelperService.toApiRoomProperties(room);
  }

  // TODO Wenn roomId undefined,entweder null zurück oder undefined verbieten, d. h. Interface ändern!
  async getRoom(roomId?: string): Promise<Room> {
    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(`No room with Id "${roomId}" found`);
    }

    return RoomHelperService.toApiRoom(room);
  }

  /**
   * Get list of all rooms your account is associated with
   */
  async getRooms(): Promise<Rooms> {
    return (await this.getRoomsSortedByLastEvent(this.matrix.client)).map(RoomHelperService.toApiRoom);
  }

  /**
   * Öffentliche Räume aus dem Raumverzeichnis des eigenen Homeservers, angereichert um
   * Mitgliederzahl, Alias und ob der aktuelle Nutzer bereits beigetreten ist.
   *
   * Gemeinsamer Discovery-Kern für UI (Suchdialog) und Testtreiber: {@link getPublicRooms}
   * baut darauf auf. So nutzen UI und Testtreiber-Endpunkt dieselbe Verzeichnis-Abfrage.
   */
  async getPublicRoomInfos(): Promise<PublicRoomInfo[]> {
    let publicChunk: IPublicRoomsChunkRoom[] = [];
    try {
      const response = await this.matrix.client.publicRooms({ limit: PUBLIC_ROOMS_DIRECTORY_LIMIT });
      publicChunk = response.chunk;
    } catch (error) {
      console.warn('getPublicRoomInfos: Abruf des öffentlichen Raumverzeichnisses fehlgeschlagen', error);
    }

    return publicChunk.map(chunk => ({
      room: RoomService.publicRoomChunkToApiRoom(chunk),
      canonicalAlias: chunk.canonical_alias ?? undefined,
      memberCount: chunk.num_joined_members ?? 0,
      joined: this.matrix.client.getRoom(chunk.room_id)?.getMyMembership() === KnownMembership.Join
    }));
  }

  /**
   * Wie {@link getRooms}, jedoch zusätzlich angereichert um die öffentlichen Räume aus dem
   * Raumverzeichnis des Homeservers (gemeinsamer Kern {@link getPublicRoomInfos}). Lokale
   * Räume haben Vorrang; Verzeichnis-Einträge werden nur ergänzt, wenn ihre roomId nicht
   * bereits lokal bekannt ist. Dedupliziert über roomId.
   *
   * Dies erfüllt den getRooms-Vertrag der TiMessengerTestTreiber.yaml ("This includes public
   * rooms") und ermöglicht, dass ein anderer User einen frisch erstellten öffentlichen Raum
   * findet, ohne ihm bereits beigetreten zu sein.
   */
  async getPublicRooms(): Promise<Rooms> {
    const localRooms = (await this.getRoomsSortedByLastEvent(this.matrix.client)).map(RoomHelperService.toApiRoom);
    const knownRoomIds = new Set(localRooms.map(room => room.roomId));

    const directoryRooms = (await this.getPublicRoomInfos())
      .filter(info => !knownRoomIds.has(info.room.roomId))
      .map(info => info.room);

    return [...localRooms, ...directoryRooms];
  }

  /**
   * Konvertiert einen Eintrag aus dem öffentlichen Raumverzeichnis (matrix-js-sdk) in das
   * Room-DTO der Testtreiber-API. Verzeichniseinträge liefern kein vollständiges Raummodell,
   * daher bleibt members leer und es werden nur die verfügbaren Felder gesetzt.
   */
  private static publicRoomChunkToApiRoom(chunk: IPublicRoomsChunkRoom): Room {
    let joinRule: Room['joinRule'];
    if (chunk.join_rule === JoinRule.Knock) {
      joinRule = 'knock';
    } else if (chunk.join_rule === JoinRule.Public) {
      joinRule = 'public';
    }

    return {
      roomId: chunk.room_id,
      name: chunk.name ?? chunk.canonical_alias ?? chunk.room_id,
      members: [],
      theme: chunk.topic,
      roomAccess: chunk.join_rule === JoinRule.Public || chunk.join_rule === undefined ? 'public' : 'private',
      joinRule,
      historyVisibility: chunk.world_readable ? 'world_readable' : undefined
    };
  }

  /**
   * Invite users into the room
   * @param roomId ID of the room
   * @param roomInvite Array of MxIds to invite
   */
  async inviteIntoRoom(roomId: string, roomInvite: RoomInvite): Promise<Room> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'inviteIntoRoom'));
    }
    if (!roomInvite) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomInvite', 'inviteIntoRoom'));
    }

    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(`Could not find Room with ID: ${roomId} when calling inviteIntoRoom`);
    }

    const joinedRoomMemberIds = room
      .getMembers()
      .filter(member => member.membership === KnownMembership.Join)
      .map(member => member.userId);
    const invitedUserIds = roomInvite.map(invite => invite.mxid);

    const containsInvite = this.arrayIntersection(joinedRoomMemberIds, invitedUserIds);

    if (containsInvite.length > 0) {
      const userOrUsers = containsInvite.length === 1 ? 'user' : 'users';
      const isOrAre = containsInvite.length === 1 ? 'is' : 'are';
      throw new Error(`The invited ${userOrUsers} ${containsInvite.join(', ')} ${isOrAre} already in the room.`);
    }

    try {
      await inviteAndWaitAll(this.matrix.client, roomId, invitedUserIds);
    } catch (error) {
      console.error('Fehler beim Invite/Warten', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('InviteIntoRoomError', error);
    }

    await sleep(SLEEP_TIMEOUT);
    return RoomHelperService.toApiRoom(room);
  }

  /**
   * Join the room
   * @param roomId ID of the room
   */
  async joinRoom(roomId: string): Promise<Room> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'joinRoom'));
    }

    const isRoomForgotten = this.isRoomForgotten(roomId);
    try {
      this.unsetRoomForgotten(roomId);
      const room = await joinRoomAndWait(this.matrix.client, roomId);

      await sleep(SLEEP_TIMEOUT);
      return RoomHelperService.toApiRoom(room);
    } catch (error) {
      if (isRoomForgotten) {
        this.setRoomForgotten(roomId);
      }
      console.error('Fehler beim Join/Warten', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('JoinRoomError', error);
    }
  }

  /**
   * Leave the room. A_28342
   * @param roomId ID of the room
   */
  async leaveRoom(roomId: string): Promise<Room | null> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'leaveRoom'));
    }

    try {
      const client = this.matrix.client;
      let room = client.getRoom(roomId);
      // Verlasse Raum nur, wenn nicht bereits verlassen.
      if (room?.getMyMembership() !== KnownMembership.Leave) {
        room = await leaveRoomAndWait(client, roomId);
        await sleep(SLEEP_TIMEOUT);
      }

      return room ? RoomHelperService.toApiRoom(room) : null;
    } catch (error) {
      console.error('Fehler beim Leave/Wait', error);
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('LeaveRoomError', error);
    }
  }

  /**
   * get all room states
   * @param roomId ID of the room
   */
  async getRoomStates(roomId: string): Promise<RoomStates> {
    if (!roomId) {
      throw new Error(format(REQ_PARAM_ERROR, 'roomId', 'getRoomStates'));
    }

    const events = await this.matrix.client.roomState(roomId);

    return events.map(this.iStateEventToApiRoomState);
  }

  isRoomForgotten(roomId: string): boolean {
    return sessionStorage.getItem(`forgotten_${roomId}`) === 'true';
  }

  /****************************************************************************
   * Helper
   ****************************************************************************/

  private iStateEventToApiRoomState(event: IStateEventWithRoomId): RoomState {
    return {
      eventId: event.event_id,
      roomId: event.room_id,
      type: event.type,
      sender: event.sender,
      content: JSON.stringify(event.content)
    };
  }

  /**
   * Liefert alle Rooms des Clients, sortiert nach Timestamp des letzten Events (neueste zuerst).
   * Verwendet die neue API: room.getLiveTimeline().getEvents()
   */
  private async getRoomsSortedByLastEvent(client: MatrixClient): Promise<MatrixRoom[]> {
    // Berücksichtige auch Räume im Zustand Leave.
    const rooms = [...(await client.syncLeftRooms()), ...client.getRooms()];
    // slice() damit wir das Original-Array nicht mutieren
    return rooms.slice().sort((a, b) => this.getLastEventTs(b) - this.getLastEventTs(a));
  }

  /** Hilfsfunktion: Timestamp des letzten relevanten Events eines Rooms */
  private getLastEventTs(room: MatrixRoom): number {
    try {
      const liveTimeline = room.getLiveTimeline?.();
      if (!liveTimeline) {
        return 0;
      }

      const events: MatrixEvent[] = liveTimeline.getEvents ? liveTimeline.getEvents() : [];

      // Rückwärts durchgehen und erstes Event mit gültigem Timestamp zurückgeben
      for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i];
        if (!ev) continue;
        // ev.getTs() liefert den Zeitstempel (ms). Fallback auf origin_server_ts falls nötig.
        const ts = (typeof ev.getTs === 'function' && ev.getTs()) || ev.event?.origin_server_ts || 0;

        if (ts && ts > 0) {
          return ts;
        }
      }

      return 0;
    } catch (error) {
      console.warn('getLastEventTs fehlgeschlagen', error);
      // Falls irgendeine API-Variante nicht vorhanden ist → 0
      return 0;
    }
  }

  private arrayIntersection(a: string[], b: string[]): string[] {
    const setA = new Set(a);
    const common: string[] = [];

    for (const item of b) {
      if (setA.has(item) && !common.includes(item)) {
        common.push(item);
      }
    }

    return common;
  }

  /********************************************************************************************************************
   * Vergessene Räume, zur unmittelbaren Wirksamkeit (nicht erst nach Server-Roundtrip/90 s)
   ********************************************************************************************************************/

  private setRoomForgotten(roomId: string): void {
    sessionStorage.setItem(`forgotten_${roomId}`, 'true');
  }

  private unsetRoomForgotten(roomId: string): void {
    const isRoomForgotten = sessionStorage.getItem(`forgotten_${roomId}`);
    if (isRoomForgotten) {
      // TODO: removeItem funktioniert hier offenbar nicht – Ursache analysieren. Bekannte technische Schuld, bereits in Arbeit.
      sessionStorage.removeItem(isRoomForgotten);

      // Workaround
      if (this.isRoomForgotten(roomId)) {
        sessionStorage.setItem(`forgotten_${roomId}`, 'false');
      }
    }
  }
}
